import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  toast,
  useDebounceCallback,
} from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { AlertTriangle, Clock, Loader2, PlusIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import {
  canUseMerchantPermission,
  type PosLotItem,
  type PosSerialItem,
  type ShopSellableCatalogRow,
  shopCatalogApi,
} from "@/modules/merchant/presentation/api/client";
import {
  useClockOutShopShiftAssignment,
  useExchangeRatesQuery,
  useOpenShopShiftMutation,
  usePosCreateHoldMutation,
  usePosScanResolveMutation,
  useShopBranchesListQuery,
  useShopShiftAssignmentsListQuery,
  useShopShiftStaffOptionsQuery,
} from "@/modules/merchant/presentation/api/queries";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import {
  formatPosBranchDisplayLabel,
  formatPosBranchOptionLabel,
} from "@/modules/merchant/presentation/lib/merchant-pos-branch-label";
import {
  getStoredMerchantPosBranchId,
  setStoredMerchantPosBranchId,
} from "@/modules/merchant/presentation/lib/merchant-pos-branch-storage";
import { useMerchantPosSimpleMode } from "@/modules/merchant/presentation/lib/merchant-pos-simple-mode";
import { posToastError } from "@/modules/merchant/presentation/lib/pos-user-messages";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShopsStore } from "@/modules/merchant/presentation/store/merchant-shops-store";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { ShopAddSellableProductModal } from "@/modules/merchant/presentation/ui/ShopAddSellableProductModal";
import {
  buildPosCheckoutLinesFromCart,
  cartNetSubtotalInInvoiceCurrency,
  newPosLineInstanceId,
  ShopPosCheckoutModal,
} from "@/modules/merchant/presentation/ui/ShopPosCheckoutModal";
import { ShopPosLotPickModal } from "@/modules/merchant/presentation/ui/ShopPosLotPickModal";
import { ShopPosProductBrowse } from "@/modules/merchant/presentation/ui/ShopPosProductBrowse";
import { ShopPosSerialPickModal } from "@/modules/merchant/presentation/ui/ShopPosSerialPickModal";
import { ShopPosCartPanel } from "@/modules/merchant/presentation/ui/shop-pos/ShopPosCartPanel";
import { ShopPosMainContent } from "@/modules/merchant/presentation/ui/shop-pos/ShopPosMainContent";
import { ShopPosMobileCheckoutBar } from "@/modules/merchant/presentation/ui/shop-pos/ShopPosMobileCheckoutBar";
import { ShopPosPageToolbar } from "@/modules/merchant/presentation/ui/shop-pos/ShopPosPageToolbar";
import { ShopPosScanAside } from "@/modules/merchant/presentation/ui/shop-pos/ShopPosScanAside";
import { ShopPosScanErrorDialog } from "@/modules/merchant/presentation/ui/shop-pos/ShopPosScanErrorDialog";
import { ShopPosShiftGate } from "@/modules/merchant/presentation/ui/shop-pos/ShopPosShiftGate";
import {
  posCartLineKey,
  type ShopPosCartLineExt,
} from "@/modules/merchant/presentation/ui/shop-pos/shop-pos-cart";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import {
  DEFAULT_SHOP_CURRENCY,
  formatPrice,
  isShopCurrency,
  SHOP_CURRENCIES,
  type ShopCurrency,
} from "@/shared/lib/currency";
import {
  formatDateForInput,
  formatDateTimeLocal,
} from "@/shared/lib/date-time";
import { buildDirectRateMapFromEffectiveRows } from "@/shared/lib/exchange-rate";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";

/** ຄວາມຍາວສູງສຸດຂອງລະຫັດສະແກນ (barcode / serial) — ບໍ່ກັ່ນຕາມຮູບແບບເພື່ອຮອງຮັບ EAN-128, ລະຫັດຮ້ານ, serial ຕ່າງໆ */
const POS_SCAN_MAX_LEN = 100;

const POS_ROUTE = "/app/shops/$shopId/pos" as const;

/** DB ຄ່າເກົ່າ `serial` ທຳງານຄື lot_serial ຈົນກວ່າ migration ຈະອັບເດດ */
function skuUsesLotSerialFlow(m: string | undefined): boolean {
  return m === "lot_serial" || m === "serial";
}

export function ShopPosPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const barcodeFieldId = useId();
  const posStaffSelectId = useId();
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const nav = useNavigate({ from: POS_ROUTE });
  const posSearch = (useSearch({ strict: false }) ?? {}) as { focus?: string };
  const { user } = useAuthState();
  const { currentShop, currentPermissions, roleName } =
    useMerchantShops(shopId);
  const shops = useMerchantShopsStore((s) => s.shops);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;

  const { simpleMode, toggleSimpleMode } = useMerchantPosSimpleMode();
  const [clearCartConfirmOpen, setClearCartConfirmOpen] = useState(false);

  const membershipsForShop = useMemo(
    () => (shopId ? shops.filter((x) => x.shopId === shopId) : []),
    [shops, shopId],
  );
  const hasShopWideBranchAccess = useMemo(
    () => membershipsForShop.some((m) => m.branchId == null),
    [membershipsForShop],
  );
  const accessibleScopedBranches = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of membershipsForShop) {
      if (row.branchId) {
        m.set(
          row.branchId,
          row.branchName?.trim() || `${row.branchId.slice(0, 8)}…`,
        );
      }
    }
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [membershipsForShop]);

  const canListBranchesForPos = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.shop_staff.read,
    verifiedLevel,
  );

  const needBranchesFromApi =
    shopId != null && hasShopWideBranchAccess && canListBranchesForPos;

  const branchesListQuery = useShopBranchesListQuery(
    needBranchesFromApi ? shopId : undefined,
  );

  const posBranchOptions = useMemo(() => {
    const shopName = currentShop?.shopName?.trim() ?? null;
    if (!shopId) return [];
    if (hasShopWideBranchAccess && canListBranchesForPos) {
      const items = branchesListQuery.data ?? [];
      return items.map((b) => ({
        id: b.id,
        label: formatPosBranchOptionLabel(b.name, shopName, b.isActive),
      }));
    }
    if (!hasShopWideBranchAccess && accessibleScopedBranches.length > 0) {
      return accessibleScopedBranches.map((b) => ({
        id: b.id,
        label: formatPosBranchDisplayLabel(b.name, shopName),
      }));
    }
    return [];
  }, [
    shopId,
    currentShop?.shopName,
    hasShopWideBranchAccess,
    canListBranchesForPos,
    branchesListQuery.data,
    accessibleScopedBranches,
  ]);

  const [selectedPosBranchId, setSelectedPosBranchId] = useState<string | null>(
    null,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset ສາຂາເມື່ອປ່ຽນຮ້ານ
  useEffect(() => {
    setSelectedPosBranchId(null);
  }, [shopId]);

  useEffect(() => {
    if (posBranchOptions.length === 0) {
      setSelectedPosBranchId(null);
      return;
    }
    setSelectedPosBranchId((prev) => {
      if (prev != null && posBranchOptions.some((o) => o.id === prev)) {
        return prev;
      }
      if (shopId) {
        const stored = getStoredMerchantPosBranchId(shopId);
        if (stored && posBranchOptions.some((o) => o.id === stored)) {
          return stored;
        }
      }
      return posBranchOptions[0]?.id ?? null;
    });
  }, [posBranchOptions, shopId]);

  /** ບັນທຶກຕອນມີສາຂາຈິງເທົ່ານັ້ນ — ຢ່າລຶບ sessionStorage ເມື່ອ prev === null (ຫຼັງ refresh ກ່ອນ posBranchOptions ໂຫຼດ) */
  useEffect(() => {
    if (!shopId || selectedPosBranchId == null) return;
    setStoredMerchantPosBranchId(shopId, selectedPosBranchId);
  }, [shopId, selectedPosBranchId]);

  const branchId = selectedPosBranchId;
  const branchPickerLoading =
    needBranchesFromApi &&
    branchesListQuery.isLoading &&
    posBranchOptions.length === 0;

  const branchTitle =
    branchId != null
      ? posBranchOptions.find((o) => o.id === branchId)?.label?.trim() ||
        (currentShop?.branchName
          ? formatPosBranchDisplayLabel(
              currentShop.branchName,
              currentShop.shopName,
            )
          : null) ||
        `${branchId.slice(0, 8)}…`
      : null;

  const canSell = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.pos.sell,
    verifiedLevel,
  );
  const canReadPos = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.pos.read,
    verifiedLevel,
  );
  const canReadPromotions =
    hasShopFeature(currentShop?.featureFlags, "promotions") &&
    canUseMerchantPermission(
      currentPermissions,
      MerchantPermissions.promotions.read,
      verifiedLevel,
    );
  const canReadMarketplace =
    hasShopFeature(currentShop?.featureFlags, "marketplace_sync") &&
    canUseMerchantPermission(
      currentPermissions,
      MerchantPermissions.marketplace.read,
      verifiedLevel,
    );
  const canReadClaims =
    hasShopFeature(currentShop?.featureFlags, "claims") &&
    canUseMerchantPermission(
      currentPermissions,
      MerchantPermissions.claims.read,
      verifiedLevel,
    );
  const canReadSettlement =
    hasShopFeature(currentShop?.featureFlags, "settlement") &&
    canUseMerchantPermission(
      currentPermissions,
      MerchantPermissions.settlement.read,
      verifiedLevel,
    );

  const canPosProvisionProduct =
    hasShopFeature(currentShop?.featureFlags, "provision_sellable") && canSell;

  const canReadShifts = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.shifts.read,
    verifiedLevel,
  );
  const canClockShift = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.shifts.clock,
    verifiedLevel,
  );
  const canManageShifts = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.shifts.manage,
    verifiedLevel,
  );

  const [selectedPosShopStaffId, setSelectedPosShopStaffId] = useState<
    string | null
  >(null);

  const workDateToday = formatDateForInput(new Date());

  const posStaffOptionsQuery = useShopShiftStaffOptionsQuery(
    shopId,
    branchId && canReadShifts ? branchId : undefined,
    { forPosClock: !canManageShifts },
  );

  const shiftAssignmentsQuery = useShopShiftAssignmentsListQuery(
    shopId,
    shopId && branchId && canReadShifts && selectedPosShopStaffId
      ? {
          branchId,
          from: workDateToday,
          to: workDateToday,
          shopStaffId: selectedPosShopStaffId,
        }
      : null,
  );

  const activeShiftAssignment = useMemo(() => {
    const items = shiftAssignmentsQuery.data?.items ?? [];
    return items.find(
      (a) =>
        a.status !== "canceled" && a.clockInAt != null && a.clockOutAt == null,
    );
  }, [shiftAssignmentsQuery.data?.items]);

  const shiftsFeatureOn = hasShopFeature(currentShop?.featureFlags, "shifts");

  const shiftGateReady =
    canSell &&
    branchId != null &&
    (shiftsFeatureOn ? canReadShifts && canClockShift : true);

  const posSalesUnlocked = shiftsFeatureOn
    ? shiftGateReady &&
      selectedPosShopStaffId != null &&
      activeShiftAssignment != null
    : Boolean(canSell && branchId != null);

  /** ຍັງບໍ່ເປີດກະ — ບໍ່ສະແດງ POS/ສະແກນຊຳລະ; ສະແດງແຕ່ alert ໃຫ້ເປີດກະກ່ອນ (ສະເພາະເມື່ອ feature ກະເປີດໃຫ້ຮ້ານ) */
  const blockedUntilShiftOpen =
    shiftsFeatureOn && shiftGateReady && !posSalesUnlocked;

  const openShift = useOpenShopShiftMutation(shopId);
  const clockOutShift = useClockOutShopShiftAssignment(shopId);

  const [barcode, setBarcode] = useState("");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [cart, setCart] = useState<ShopPosCartLineExt[]>([]);
  const [billDiscount, setBillDiscount] = useState(0);
  const [billDiscountPercent, setBillDiscountPercent] = useState(0);
  const [billDiscountUsePercent, setBillDiscountUsePercent] = useState(false);
  const [lotPickOpen, setLotPickOpen] = useState(false);
  const [lotPickRow, setLotPickRow] = useState<ShopSellableCatalogRow | null>(
    null,
  );
  const [serialPickOpen, setSerialPickOpen] = useState(false);
  const [serialPickRow, setSerialPickRow] =
    useState<ShopSellableCatalogRow | null>(null);
  const [lastScannedLineKey, setLastScannedLineKey] = useState<string | null>(
    null,
  );
  const [scanErrorOpen, setScanErrorOpen] = useState(false);
  const [failedBarcode, setFailedBarcode] = useState("");
  const [addProductModalOpen, setAddProductModalOpen] = useState(false);
  const [shiftCloseConfirmOpen, setShiftCloseConfirmOpen] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset ພະນັກງານທີ່ເລືອກເມື່ອປ່ຽນຮ້ານ/ສາຂາ
  useEffect(() => {
    setSelectedPosShopStaffId(null);
  }, [shopId, branchId]);

  const cartRef = useRef<ShopPosCartLineExt[]>([]);
  cartRef.current = cart;

  const scanLockRef = useRef(false);
  const scanResolve = usePosScanResolveMutation(shopId, branchId ?? undefined);
  const createHold = usePosCreateHoldMutation(shopId, branchId ?? undefined);
  const exchangeRatesQuery = useExchangeRatesQuery(shopId);
  const [invoiceCurrency, setInvoiceCurrency] = useState<ShopCurrency>(
    DEFAULT_SHOP_CURRENCY,
  );

  useEffect(() => {
    if (!scanErrorOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [scanErrorOpen]);

  useEffect(() => {
    if (!posSalesUnlocked) setCheckoutOpen(false);
  }, [posSalesUnlocked]);

  useEffect(() => {
    if (!posSalesUnlocked) setShiftCloseConfirmOpen(false);
  }, [posSalesUnlocked]);

  const addToCart = useCallback(
    (row: ShopSellableCatalogRow) => {
      if (!posSalesUnlocked) {
        toast.error("ເປີດກະກ່ອນ — ຈຶ່ງຈະເພີ່ມສິນຄ້າໄດ້");
        return;
      }
      if (skuUsesLotSerialFlow(row.trackingMode)) {
        setSerialPickRow(row);
        setSerialPickOpen(true);
        return;
      }
      if (row.trackingMode === "lot") {
        setLotPickRow(row);
        setLotPickOpen(true);
        return;
      }
      const prev = cartRef.current;
      const totalPlain = prev
        .filter((l) => !l.lotId && !l.serialId && l.row.id === row.id)
        .reduce((s, l) => s + l.qty, 0);
      const nextTotal = totalPlain + 1;
      if (row.quantityOnHand !== null && nextTotal > row.quantityOnHand) {
        toast.error("ສະຕ໋ອກບໍ່ພໍ");
        return;
      }
      setCart((p) => {
        const j = p.findLastIndex(
          (l) => !l.lotId && !l.serialId && l.row.id === row.id,
        );
        if (j >= 0) {
          const next = [...p];
          const cur = next[j];
          if (!cur) return p;
          next[j] = { ...cur, qty: cur.qty + 1 };
          queueMicrotask(() => setLastScannedLineKey(cur.lineInstanceId));
          return next;
        }
        const id = newPosLineInstanceId();
        queueMicrotask(() => setLastScannedLineKey(id));
        return [...p, { row, qty: 1, lineInstanceId: id }];
      });
    },
    [posSalesUnlocked],
  );

  const commitLotLineToCart = useCallback(
    (row: ShopSellableCatalogRow, lot: PosLotItem) => {
      if (!posSalesUnlocked) {
        toast.error("ເປີດກະກ່ອນ — ຈຶ່ງຈະເພີ່ມສິນຄ້າໄດ້");
        return;
      }
      const prev = cartRef.current;
      const totalSameLot = prev
        .filter((l) => l.row.id === row.id && l.lotId === lot.id)
        .reduce((s, l) => s + l.qty, 0);
      const nextTotal = totalSameLot + 1;
      const cap = lot.qty;
      if (nextTotal > cap) {
        toast.error("ສະຕ໋ອກບໍ່ພໍ");
        return;
      }
      const exp = lot.expiryDate
        ? new Date(`${lot.expiryDate}T12:00:00`).toLocaleDateString("lo-LA")
        : null;
      const lotSummary = exp ? `${lot.lotNo} · ໝົດ ${exp}` : lot.lotNo;
      setCart((p) => {
        const j = p.findLastIndex(
          (l) => l.row.id === row.id && l.lotId === lot.id,
        );
        if (j >= 0) {
          const next = [...p];
          const cur = next[j];
          if (!cur) return p;
          next[j] = { ...cur, qty: cur.qty + 1 };
          queueMicrotask(() => setLastScannedLineKey(cur.lineInstanceId));
          return next;
        }
        const id = newPosLineInstanceId();
        queueMicrotask(() => setLastScannedLineKey(id));
        return [
          ...p,
          {
            row,
            qty: 1,
            lineInstanceId: id,
            lotId: lot.id,
            lotSummary,
            lotQtyCap: cap,
          },
        ];
      });
    },
    [posSalesUnlocked],
  );

  const commitSerialToCart = useCallback(
    (row: ShopSellableCatalogRow, serial: PosSerialItem) => {
      if (!posSalesUnlocked) {
        toast.error("ເປີດກະກ່ອນ — ຈຶ່ງຈະເພີ່ມສິນຄ້າໄດ້");
        return;
      }
      if (cartRef.current.some((l) => l.serialId === serial.id)) {
        toast.error("Serial ນີ້ຢູ່ໃນກະຕ່າແລ້ວ");
        return;
      }
      setCart((p) => {
        const id = newPosLineInstanceId();
        queueMicrotask(() => setLastScannedLineKey(id));
        return [
          ...p,
          {
            row,
            qty: 1,
            lineInstanceId: id,
            serialId: serial.id,
            serialNo: serial.serialNo,
            serialLabel:
              serial.lotNo != null && serial.lotNo !== ""
                ? `${serial.serialNo} · Lot ${serial.lotNo}`
                : serial.serialNo,
          },
        ];
      });
    },
    [posSalesUnlocked],
  );

  /** ສະແກນ: scan-resolve (unit → serial_no → lot_scan → sku barcode) */
  const runScanForCode = useCallback(
    async (raw: string) => {
      const b = raw.trim();
      if (!b) return;
      if (b.length > POS_SCAN_MAX_LEN) {
        toast.error(`ລະຫັດຍາວເກີນໄປ (ສູງສຸດ ${POS_SCAN_MAX_LEN} ຕົວ)`);
        return;
      }
      if (scanLockRef.current) return;
      scanLockRef.current = true;
      try {
        if (!shopId || !branchId) {
          toast.error("ຕ້ອງມີຮ້ານ ແລະ ສາຂາເພື່ອສະແກນ");
          return;
        }
        if (!posSalesUnlocked) {
          toast.error("ເປີດກະກ່ອນ — ຈຶ່ງຈະສະແກນເພີ່ມສິນຄ້າໄດ້");
          return;
        }
        const resolved = await scanResolve.mutateAsync(b);
        if (resolved.kind === "unit" || resolved.kind === "serial_no") {
          const row = await shopCatalogApi.getSellableSku(
            shopId,
            resolved.shopProductSkuId,
          );
          if (!skuUsesLotSerialFlow(row.trackingMode)) {
            setFailedBarcode(b);
            setScanErrorOpen(true);
            return;
          }
          if (cartRef.current.some((l) => l.serialId === resolved.serialId)) {
            toast.error("Serial ນີ້ຢູ່ໃນກະຕ່າແລ້ວ");
            setBarcode("");
            queueMicrotask(() => barcodeInputRef.current?.focus());
            return;
          }
          const lotPart =
            resolved.lotNo && resolved.lotExpiryDate
              ? `${resolved.lotNo} · ໝົດ ${new Date(`${resolved.lotExpiryDate}T12:00:00`).toLocaleDateString("lo-LA")}`
              : (resolved.lotNo ?? undefined);
          const serialLabel =
            lotPart != null && lotPart !== ""
              ? `${resolved.serialNo} · Lot ${lotPart}`
              : resolved.serialNo;
          setCart((p) => {
            const id = newPosLineInstanceId();
            queueMicrotask(() => setLastScannedLineKey(id));
            return [
              ...p,
              {
                row,
                qty: 1,
                lineInstanceId: id,
                serialId: resolved.serialId,
                serialNo: resolved.serialNo,
                serialLabel,
              },
            ];
          });
          setBarcode("");
          queueMicrotask(() => barcodeInputRef.current?.focus());
          return;
        }
        if (resolved.kind === "lot") {
          const row = await shopCatalogApi.getSellableSku(
            shopId,
            resolved.shopProductSkuId,
          );
          if (row.trackingMode !== "lot") {
            setFailedBarcode(b);
            setScanErrorOpen(true);
            return;
          }
          const lotItem: PosLotItem = {
            id: resolved.lotId,
            lotNo: resolved.lotNo,
            qty: resolved.lotQty,
            expiryDate: resolved.lotExpiryDate,
            manufacturedDate: resolved.lotManufacturedDate,
          };
          commitLotLineToCart(row, lotItem);
          setBarcode("");
          queueMicrotask(() => barcodeInputRef.current?.focus());
          return;
        }
        if (resolved.kind === "sku_barcode") {
          if (resolved.catalogRow) {
            addToCart(resolved.catalogRow);
          }
          setBarcode("");
          queueMicrotask(() => barcodeInputRef.current?.focus());
          return;
        }
        setFailedBarcode(b);
        setScanErrorOpen(true);
      } catch (e) {
        toast.error(posToastError(e));
      } finally {
        scanLockRef.current = false;
      }
    },
    [
      scanResolve,
      addToCart,
      commitLotLineToCart,
      shopId,
      branchId,
      posSalesUnlocked,
    ],
  );

  const runScanRef = useRef(runScanForCode);
  runScanRef.current = runScanForCode;

  /** ຫຼັງສະແກນຈົບ ~90ms — ຄົ້ນແລ້ວເອີ່ມ/ເປີດ modal; ກວດຄ່າໃນຊ່ອງກັບ debounce ເພື່ອຫຼີກເລີ່ຍຫຼັງ Enter/clear */
  const debouncedAutoScan = useDebounceCallback((value: string) => {
    const live = barcodeInputRef.current?.value ?? "";
    if (live !== value) return;
    void runScanRef.current(value);
  }, 90);

  const hasMixedCurrencyCart = useMemo(() => {
    const u = new Set(cart.map((l) => l.row.currency));
    return u.size > 1;
  }, [cart]);

  const lastScannedLine = useMemo(() => {
    if (!lastScannedLineKey) return null;
    return cart.find((l) => posCartLineKey(l) === lastScannedLineKey) ?? null;
  }, [cart, lastScannedLineKey]);

  const rateMap = useMemo(() => {
    const items = exchangeRatesQuery.data?.items;
    if (!items?.length) return new Map<string, number>();
    return buildDirectRateMapFromEffectiveRows(items);
  }, [exchangeRatesQuery.data?.items]);

  const cartFxSourceCurrencies = useMemo(() => {
    const u = new Set<string>();
    for (const l of cart) u.add(l.row.currency);
    return [...u].filter(isShopCurrency);
  }, [cart]);

  useEffect(() => {
    const def = currentShop?.defaultCurrency ?? DEFAULT_SHOP_CURRENCY;
    if (cart.length === 0) {
      setInvoiceCurrency(def);
      return;
    }
    const uniq = new Set(cart.map((l) => l.row.currency));
    if (uniq.size === 1) {
      const c = cart[0]?.row.currency;
      if (c && isShopCurrency(c)) setInvoiceCurrency(c);
    }
  }, [cart, currentShop?.defaultCurrency]);

  const cartSubtotal = useMemo(() => {
    if (cart.length === 0) return 0;
    return cartNetSubtotalInInvoiceCurrency(cart, invoiceCurrency, rateMap);
  }, [cart, invoiceCurrency, rateMap]);

  const cartSubtotalFinite = Number.isFinite(cartSubtotal);

  const operatorLabel = user?.name?.trim() || user?.email?.trim() || "—";
  const shopIdShort = shopId ? shopId.slice(0, 8) : "—";

  const clearCart = useCallback(() => {
    setCart([]);
    setLastScannedLineKey(null);
    setBillDiscount(0);
    setBillDiscountPercent(0);
    setBillDiscountUsePercent(false);
  }, []);

  const prevBranchForCartRef = useRef<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset ຫຼັງປ່ຽນຮ້ານ
  useEffect(() => {
    prevBranchForCartRef.current = null;
  }, [shopId]);

  useEffect(() => {
    if (branchId == null) {
      prevBranchForCartRef.current = null;
      return;
    }
    const prev = prevBranchForCartRef.current;
    if (prev !== null && prev !== branchId) {
      clearCart();
      toast.info("ປ່ຽນສາຂາ — ລ້າງກະຕ່າ");
    }
    prevBranchForCartRef.current = branchId;
  }, [branchId, clearCart]);

  const requestClearCart = useCallback(() => {
    if (cart.length === 0) return;
    if (simpleMode) {
      setClearCartConfirmOpen(true);
      return;
    }
    clearCart();
  }, [cart.length, simpleMode, clearCart]);

  const focusBarcodeInput = useCallback(() => {
    queueMicrotask(() => barcodeInputRef.current?.focus());
  }, []);

  useEffect(() => {
    if (posSearch.focus !== "scan") return;
    if (!posSalesUnlocked) return;
    focusBarcodeInput();
    nav({ search: { focus: undefined }, replace: true });
  }, [posSearch.focus, posSalesUnlocked, focusBarcodeInput, nav]);

  const posScanAside = (
    <ShopPosScanAside
      simpleMode={simpleMode}
      barcodeInputRef={barcodeInputRef}
      barcodeFieldId={barcodeFieldId}
      barcode={barcode}
      setBarcode={setBarcode}
      debouncedAutoScan={debouncedAutoScan}
      runScanForCode={runScanForCode}
      scanPending={scanResolve.isPending}
      posSalesUnlocked={posSalesUnlocked}
      lastScannedLine={lastScannedLine}
      cartSubtotalFinite={cartSubtotalFinite}
      cartSubtotal={cartSubtotal}
      invoiceCurrency={invoiceCurrency}
      hasMixedCurrencyCart={hasMixedCurrencyCart}
      setInvoiceCurrency={setInvoiceCurrency}
      cartFxSourceCurrencies={cartFxSourceCurrencies}
      rateMap={rateMap}
      exchangeRatesAsOf={exchangeRatesQuery.data?.asOf}
      exchangeRatesLoading={exchangeRatesQuery.isLoading}
      shopId={shopId}
      billDiscountUsePercent={billDiscountUsePercent}
      setBillDiscountUsePercent={setBillDiscountUsePercent}
      billDiscountPercent={billDiscountPercent}
      setBillDiscountPercent={setBillDiscountPercent}
      billDiscount={billDiscount}
      setBillDiscount={setBillDiscount}
      focusBarcodeInput={focusBarcodeInput}
      cartLength={cart.length}
      branchId={branchId}
      canSell={canSell}
      onCheckout={() => setCheckoutOpen(true)}
      onClearCart={requestClearCart}
      className={
        simpleMode
          ? undefined
          : "lg:sticky lg:top-4 lg:w-[min(100%,22rem)] xl:w-96"
      }
    />
  );

  return (
    <RequireMerchantPermission permission={MerchantPermissions.pos.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.pos(shopId)} />
      </Header>

      {shiftsFeatureOn &&
      shiftGateReady &&
      posSalesUnlocked &&
      activeShiftAssignment != null &&
      branchId != null ? (
        <div
          className={cn(
            "border-primary/20 border-b bg-gradient-to-r from-primary/[0.07] via-card to-muted/25",
            "px-4 py-3 shadow-sm lg:px-6",
          )}
        >
          <div className="mx-auto flex w-full max-w-none flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                <Clock className="size-5" aria-hidden />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-foreground text-sm sm:text-base">
                  ກະເປີດແລ້ວ ·{" "}
                  {activeShiftAssignment.staffUserName?.trim() ||
                    activeShiftAssignment.staffUserEmail}
                </p>
                <p className="mt-0.5 text-muted-foreground text-xs sm:text-sm">
                  ເຂົ້າ:{" "}
                  {activeShiftAssignment.clockInAt
                    ? formatDateTimeLocal(activeShiftAssignment.clockInAt)
                    : "—"}
                  {branchTitle != null && branchTitle !== "" ? (
                    <span>
                      {" "}
                      ·{" "}
                      <span className="font-medium text-foreground/90">
                        {branchTitle}
                      </span>
                    </span>
                  ) : null}
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 shrink-0 border-border font-semibold"
              disabled={clockOutShift.isPending}
              onClick={() => setShiftCloseConfirmOpen(true)}
            >
              ປິດກະ
            </Button>
          </div>
        </div>
      ) : null}

      <Main
        fluid
        className={
          blockedUntilShiftOpen
            ? "flex min-h-[calc(100dvh-4.5rem)] max-w-none flex-1 flex-col px-4 py-4 lg:px-6 lg:py-5"
            : cn("max-w-none px-4 py-5 lg:px-6", simpleMode && "pb-24 lg:pb-5")
        }
      >
        <div
          className={
            blockedUntilShiftOpen
              ? "flex min-h-0 flex-1 flex-col gap-0"
              : simpleMode
                ? "flex flex-col gap-4"
                : "flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8"
          }
        >
          {/* ກະລຸກາຊຳລະ — ຕາຕະລາງ */}
          <section
            className={
              blockedUntilShiftOpen
                ? "flex min-h-0 min-w-0 flex-1 flex-col"
                : "min-w-0 flex-1"
            }
          >
            <ShopPosPageToolbar
              simpleMode={simpleMode}
              toggleSimpleMode={toggleSimpleMode}
              branchId={branchId}
              branchTitle={branchTitle}
              shopIdShort={shopIdShort}
              roleName={roleName}
              operatorLabel={operatorLabel}
              shopId={shopId}
              posBranchOptions={posBranchOptions}
              setSelectedPosBranchId={setSelectedPosBranchId}
              branchPickerLoading={branchPickerLoading}
              needBranchesFromApi={needBranchesFromApi}
              branchesListError={branchesListQuery.isError}
              hasShopWideBranchAccess={hasShopWideBranchAccess}
              canListBranchesForPos={canListBranchesForPos}
              canSell={canSell}
              shiftsFeatureOn={shiftsFeatureOn}
              blockedUntilShiftOpen={blockedUntilShiftOpen}
              canReadShifts={canReadShifts}
              canClockShift={canClockShift}
              cartLength={cart.length}
              onClearCart={requestClearCart}
              onFocusScan={focusBarcodeInput}
              canReadPos={canReadPos}
              canReadPromotions={canReadPromotions}
              canReadMarketplace={canReadMarketplace}
              canReadClaims={canReadClaims}
              canReadSettlement={canReadSettlement}
            />

            {blockedUntilShiftOpen ? (
              <ShopPosShiftGate
                posStaffSelectId={posStaffSelectId}
                selectedPosShopStaffId={selectedPosShopStaffId}
                onSelectStaff={setSelectedPosShopStaffId}
                staffOptions={posStaffOptionsQuery.data?.items}
                staffLoading={posStaffOptionsQuery.isLoading}
                shiftLoading={shiftAssignmentsQuery.isLoading}
                shiftError={shiftAssignmentsQuery.isError}
                openPending={openShift.isPending}
                onOpenShift={() => {
                  if (!branchId || !selectedPosShopStaffId) return;
                  void openShift.mutateAsync({
                    branchId,
                    shopStaffId: selectedPosShopStaffId,
                    workDate: workDateToday,
                  });
                }}
              />
            ) : null}

            {!blockedUntilShiftOpen ? (
              <ShopPosMainContent
                simpleMode={simpleMode}
                browse={
                  shopId && branchId ? (
                    <ShopPosProductBrowse
                      shopId={shopId}
                      branchId={branchId}
                      compact={simpleMode}
                      collapsibleOnMobile={simpleMode}
                      disabled={!posSalesUnlocked}
                      onSelectProduct={addToCart}
                    />
                  ) : null
                }
                cart={
                  <ShopPosCartPanel
                    cart={cart}
                    setCart={setCart}
                    simpleMode={simpleMode}
                    lastScannedLineKey={lastScannedLineKey}
                    shopId={shopId}
                    branchId={branchId}
                    featureFlags={currentShop?.featureFlags}
                    posSalesUnlocked={posSalesUnlocked}
                  />
                }
                aside={posScanAside}
              />
            ) : null}
          </section>

          {!blockedUntilShiftOpen && !simpleMode ? posScanAside : null}
        </div>
      </Main>

      {!blockedUntilShiftOpen && simpleMode ? (
        <ShopPosMobileCheckoutBar
          cartSubtotalFinite={cartSubtotalFinite}
          cartSubtotal={cartSubtotal}
          invoiceCurrency={invoiceCurrency}
          cartLength={cart.length}
          branchId={branchId}
          canSell={canSell}
          posSalesUnlocked={posSalesUnlocked}
          onFocusScan={focusBarcodeInput}
          onCheckout={() => setCheckoutOpen(true)}
          onClearCart={requestClearCart}
        />
      ) : null}

      <ShopPosScanErrorDialog
        open={scanErrorOpen}
        onClose={() => setScanErrorOpen(false)}
        failedBarcode={failedBarcode}
        shopId={shopId}
        shopIdShort={shopIdShort}
        canAddProduct={canPosProvisionProduct}
        onRetryScan={focusBarcodeInput}
        onAddProduct={() => setAddProductModalOpen(true)}
      />

      {shopId ? (
        <ShopAddSellableProductModal
          open={addProductModalOpen}
          onOpenChange={setAddProductModalOpen}
          shopId={shopId}
          prefillBarcode={failedBarcode}
          prefillBranchId={branchId ?? undefined}
          onSuccess={() => {
            setBarcode("");
            queueMicrotask(() => barcodeInputRef.current?.focus());
          }}
        />
      ) : null}

      <Dialog
        open={clearCartConfirmOpen}
        onOpenChange={setClearCartConfirmOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>ຍົກເລີກລາຍການ?</DialogTitle>
            <DialogDescription className="text-left">
              ລ້າງສິນຄ້າທັງໝົດໃນກະຕ່າ — ຕ້ອງເລີ່ມສະແກນໃໝ່
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setClearCartConfirmOpen(false)}
            >
              ກັບຄືນ
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="font-semibold"
              onClick={() => {
                clearCart();
                setClearCartConfirmOpen(false);
                focusBarcodeInput();
              }}
            >
              ລ້າງກະຕ່າ
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={shiftCloseConfirmOpen}
        onOpenChange={setShiftCloseConfirmOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>ຢືນຢັນປິດກະ</DialogTitle>
            <DialogDescription className="text-left">
              {cart.length > 0
                ? "ກະຕ່າຍັງມີສິນຄ້າ — ຫຼັງປິດກະຈະບໍ່ສາມາດຊຳລະຈົນກວ່າເປີດກະອີກ. ຕ້ອງການປິດກະບໍ່?"
                : "ຕ້ອງການປິດກະ ແລະ ບັນທຶກເວລາອອກສຳລັບພະນັກງານຄົນນີ້ບໍ່?"}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShiftCloseConfirmOpen(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="button"
              className="font-semibold"
              disabled={clockOutShift.isPending}
              onClick={() => {
                if (!activeShiftAssignment?.id) return;
                void clockOutShift
                  .mutateAsync(activeShiftAssignment.id)
                  .then(() => {
                    setShiftCloseConfirmOpen(false);
                    if (cartRef.current.length > 0) {
                      toast.info(
                        "ປິດກະແລ້ວ — ຊຳລະບໍ່ໄດ້ຈົນກວ່າເປີດກະອີກ (ລ້າງກະຕ່າຖ້າຕ້ອງການ)",
                      );
                    }
                  });
              }}
            >
              {clockOutShift.isPending ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  ກຳລັງປິດ…
                </>
              ) : (
                "ປິດກະ"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {shopId && branchId && posSalesUnlocked ? (
        <>
          <ShopPosLotPickModal
            open={lotPickOpen}
            onOpenChange={(o) => {
              setLotPickOpen(o);
              if (!o) setLotPickRow(null);
            }}
            shopId={shopId}
            branchId={branchId}
            row={lotPickRow}
            onPick={(row, lot) => {
              commitLotLineToCart(row, lot);
            }}
          />
          <ShopPosSerialPickModal
            open={serialPickOpen}
            onOpenChange={(o) => {
              setSerialPickOpen(o);
              if (!o) setSerialPickRow(null);
            }}
            shopId={shopId}
            branchId={branchId}
            row={serialPickRow}
            onPick={(row, serial) => {
              commitSerialToCart(row, serial);
            }}
          />
          <ShopPosCheckoutModal
            open={checkoutOpen}
            onOpenChange={setCheckoutOpen}
            shopId={shopId}
            branchId={branchId}
            cart={cart}
            invoiceCurrency={invoiceCurrency}
            rateMap={rateMap}
            hasMixedCurrencyCart={hasMixedCurrencyCart}
            exchangeRatesLoading={exchangeRatesQuery.isLoading}
            exchangeRatesAsOf={exchangeRatesQuery.data?.asOf ?? null}
            billDiscountDraft={billDiscount}
            billDiscountPercentDraft={billDiscountPercent}
            billDiscountUsePercentDraft={billDiscountUsePercent}
            onCheckoutSuccess={clearCart}
            simpleMode={simpleMode}
          />
        </>
      ) : null}
    </RequireMerchantPermission>
  );
}
